<script setup lang="ts">
/**
 * Schorschi's own space: the avatar on its stage (shared mood, its reactions
 * to your conversation, lip sync in calls), live voice calls in three modes
 * — chat right here, standup and drive full screen — the board of things it
 * showed you, and tabs for the chat, what's going on, proposals, memory and
 * — for admins — its background work.
 *
 * ?conversation=<id> opens a conversation (links from the inbox), ?call=standup
 * or ?call=drive preselects that kind of call.
 */
import { Brain, CheckCheck, Mic, MicOff, MessageSquare, Moon, Phone, PhoneOff, Radar, Settings2 } from "@lucide/vue";
import { useMediaQuery, useResizeObserver } from "@vueuse/core";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { toast } from "vue-sonner";
import { onSchorschiEvent, schorschiKeys, useConversation, useProposals, useSchorschiRefresh, useSchorschiStatus, type SendResult, type Visual, type VoiceEvent, type VoiceMode } from "@/api/schorschi";
import ErrorState from "@/components/common/ErrorState.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import PageTabs, { type PageTabItem } from "@/components/common/PageTabs.vue";
import { isMinutes } from "@/components/schorschi/minutes";
import SchorschiAdmin from "@/components/schorschi/SchorschiAdmin.vue";
import SchorschiAvatar from "@/components/schorschi/SchorschiAvatar.vue";
import SchorschiChat from "@/components/schorschi/SchorschiChat.vue";
import SchorschiDriveView from "@/components/schorschi/SchorschiDriveView.vue";
import SchorschiFeed from "@/components/schorschi/SchorschiFeed.vue";
import SchorschiMemory from "@/components/schorschi/SchorschiMemory.vue";
import SchorschiModePicker from "@/components/schorschi/SchorschiModePicker.vue";
import SchorschiProposals from "@/components/schorschi/SchorschiProposals.vue";
import SchorschiStandupView from "@/components/schorschi/SchorschiStandupView.vue";
import VisualCard from "@/components/schorschi/VisualCard.vue";
import { MOOD_LABELS, REACTIONS, type Activity, type Mood, type Reaction, type ReactionCue } from "@/components/schorschi/moods";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { REDIAL_DELAYS, useSchorschiVoice, type TranscriptLine } from "@/composables/useSchorschiVoice";
import { schorschi as copy } from "@/copy/schorschi";
import { formatUsd } from "@/lib/format";
import { cn } from "@/lib/utils";

definePage({ meta: { title: "Schorschi", requires: { capability: "schorschi.read" } } });

const status = useSchorschiStatus();
const refresh = useSchorschiRefresh();
const tabQuery = useRouteQuery("tab", "chat");
const TAB_IDS = ["chat", "feed", "proposals", "memory", "admin"];
const tab = computed({
  get: () => (TAB_IDS.includes(tabQuery.value) && (tabQuery.value !== "admin" || status.data.value?.canAdmin !== false) ? tabQuery.value : "chat"),
  set: (v: string) => (tabQuery.value = v),
});
// ?conversation=<id> (links from the inbox) opens that conversation.
const conversationQuery = useRouteQuery("conversation", "");
const conversationId = ref<string | null>(conversationQuery.value || null);
watch(conversationQuery, (id) => {
  if (id) conversationId.value = id;
});
const conversation = useConversation(conversationId);
const openProposals = useProposals("open");

const enabled = computed(() => status.data.value?.enabled ?? true);
const canTalk = computed(() => Boolean(status.data.value?.canTalk));
const chatAvailable = computed(() => Boolean(status.data.value?.chat));

// --- the avatar: shared mood, personal expressions, activity, gestures

const personalMood = ref<{ mood: Mood; until: number } | null>(null);
const reaction = ref<ReactionCue | null>(null);
const chatBusy = ref(false);
const chatTool = ref<string | null>(null);
let cueKey = 0;

function gesture(name: Reaction) {
  reaction.value = { name, key: ++cueKey };
}
function feel(mood: Mood | undefined, seconds = 90) {
  if (mood) personalMood.value = { mood, until: Date.now() + seconds * 1000 };
}

const now = ref(Date.now());
const clock = window.setInterval(() => (now.value = Date.now()), 1000);
onBeforeUnmount(() => window.clearInterval(clock));

const mood = computed<Mood>(() => {
  if (!enabled.value) return "sleepy";
  if (personalMood.value && personalMood.value.until > now.value) return personalMood.value.mood;
  return status.data.value?.mood.mood ?? "neutral";
});

// --- voice

/** The whole transcript of the call (standup shows it all, the stage the last lines). */
const lines = ref<TranscriptLine[]>([]);
const voiceTool = ref<string | null>(null);
/** Standup: Schorschi was addressed and is about to answer. */
const addressed = ref<"started" | "queued" | null>(null);
let addressedTimer: number | undefined;
let transcriptRefresh: number | undefined;

function onVoiceEvent(e: VoiceEvent) {
  switch (e.type) {
    case "transcript":
      lines.value = [...lines.value, { role: e.role, text: e.text, at: Date.now() }].slice(-500);
      if (e.role === "assistant") addressed.value = null;
      window.clearTimeout(transcriptRefresh);
      transcriptRefresh = window.setTimeout(() => void refresh(schorschiKeys.conversation(conversationId.value)), 800);
      break;
    case "addressed": {
      if (e.state === "ended") break;
      addressed.value = e.state;
      // The line that asked: the latest one from the room.
      const i = lines.value.findLastIndex((l) => l.role === "user");
      if (i >= 0) lines.value = lines.value.map((l, j) => (j === i ? { ...l, addressed: true } : l));
      window.clearTimeout(addressedTimer);
      addressedTimer = window.setTimeout(() => (addressed.value = null), 30_000);
      gesture("nod");
      break;
    }
    case "responding":
      if (addressed.value === "queued") addressed.value = "started";
      break;
    case "expression":
      feel(e.mood);
      if (e.reaction) gesture(e.reaction);
      break;
    case "visual":
      addVisual(e.visual);
      break;
    case "tool":
      voiceTool.value = e.phase === "start" ? e.name : null;
      break;
    case "proposal":
      toast.info(`Schorschi prepared a change: ${e.summary}`, { action: { label: copy.tabs.proposals, onClick: () => (tab.value = "proposals") } });
      void refresh(schorschiKeys.proposalLists);
      break;
    case "error":
      toast.error(e.message);
      break;
    case "ended":
      voiceTool.value = null;
      addressed.value = null;
      void refresh(schorschiKeys.conversation(conversationId.value));
      void refresh(schorschiKeys.conversations);
      void refresh(schorschiKeys.status);
      break;
    default:
      break;
  }
}
onBeforeUnmount(() => {
  window.clearTimeout(addressedTimer);
  window.clearTimeout(transcriptRefresh);
});

const voice = useSchorschiVoice({ onEvent: onVoiceEvent, conversationId: () => conversationId.value });
watch(voice.conversationId, (id) => {
  if (id) conversationId.value = id;
});

/** The last message of the call's conversation when it ended: minutes after it belong to this call. */
const minutesAfterId = ref(0);
watch(voice.state, (s, before) => {
  if (s === "live" && before === "connecting") gesture("wave");
  if (s !== "live") voiceTool.value = null;
  if (s === "idle") {
    addressed.value = null;
    const messages = conversation.data.value?.messages ?? [];
    minutesAfterId.value = messages.filter((m) => !isMinutes(m)).reduce((max, m) => Math.max(max, m.id), 0);
    void refresh(schorschiKeys.conversations);
  }
});

const voiceAvailable = computed(() => Boolean(status.data.value?.voice.available) && canTalk.value && enabled.value);
const canStartCall = computed(() => voiceAvailable.value && voice.supported);
const inCall = computed(() => voice.state.value !== "idle");
const callLive = computed(() => voice.state.value === "live");
const remaining = computed(() => {
  if (!voice.startedAt.value || !voice.maxSeconds.value) return null;
  const left = Math.max(0, voice.maxSeconds.value - Math.floor((now.value - voice.startedAt.value) / 1000));
  return `${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`;
});
const caption = computed(() => [...lines.value].reverse().find((l) => l.role === "assistant")?.text ?? null);
/** What Schorschi is saying right now (from the call's data channel), else its last line. */
const liveCaption = computed(() => (voice.liveCaption.value?.role === "assistant" ? voice.liveCaption.value.text : caption.value));
/** Admins see how long Schorschi took to answer (end of speech → its voice, as heard here). */
const turnTiming = computed(() => {
  const t = voice.lastTurn.value;
  if (!t || !status.data.value?.canAdmin) return null;
  const s = (ms: number) => `${(ms / 1000).toFixed(1)} s`;
  return t.answerMs !== undefined ? `${s(t.firstMs)} · ${s(t.answerMs)}` : s(t.firstMs);
});

// Which kind of call: preselected by ?call=standup|drive (home-screen links), else chat.
const callQuery = useRouteQuery("call", "");
const isMode = (v: string): v is VoiceMode => v === "chat" || v === "standup" || v === "drive";
const callMode = ref<VoiceMode>(isMode(callQuery.value) ? callQuery.value : "chat");
// ?protocol=realtime|live asks for one (to compare Realtime and GPT-Live where GPT-Live is open).
const protocolQuery = useRouteQuery("protocol", "");
const askProtocol = computed(() => (protocolQuery.value === "realtime" || protocolQuery.value === "live" ? protocolQuery.value : undefined));
/** The full-screen view of a standup or drive call (stays open after the call for the minutes or a redial). */
const callView = ref<"standup" | "drive" | null>(null);

const coarsePointer = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;

/** From a click: the call needs the gesture for the microphone and the speaker. */
function startCall(mode: VoiceMode = callMode.value, continueIn?: string | null) {
  if (!canStartCall.value || inCall.value) return;
  lines.value = [];
  addressed.value = null;
  if (mode !== "chat") callView.value = mode;
  // In the car the whole screen is the call (where the browser allows it).
  if (mode === "drive" && coarsePointer && !document.fullscreenElement) void document.documentElement.requestFullscreen?.().catch(() => undefined);
  void voice.start(mode, continueIn, askProtocol.value);
}

function closeCallView() {
  if (inCall.value) return;
  callView.value = null;
  if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
}

const activity = computed<Activity>(() => {
  if (!enabled.value) return "offline";
  if (callLive.value) {
    const phase = voice.phase.value;
    if (voiceTool.value || phase === "working") return "working";
    if (phase === "speaking" || voice.speaking.value || voice.speakingLevel.value > 0.06) return "speaking";
    return phase === "thinking" ? "thinking" : "listening";
  }
  if (voice.state.value === "connecting" || voice.state.value === "reconnecting") return "thinking";
  if (chatBusy.value) return chatTool.value ? "working" : "thinking";
  return "idle";
});

const activityLabel = computed(() => {
  if (voice.state.value === "reconnecting") return copy.voice.reconnecting(voice.attempt.value, REDIAL_DELAYS.length);
  if (activity.value === "working") return copy.activity.working(voiceTool.value ?? chatTool.value ?? "a tool");
  if (activity.value === "thinking") return copy.activity.thinking;
  if (activity.value === "listening") return copy.activity.listening;
  if (activity.value === "speaking") return copy.activity.speaking;
  return null;
});

// --- the board: what Schorschi showed in this conversation (newest first)

const liveVisuals = ref<Visual[]>([]);
const hidden = ref(new Set<string>());
const visualKey = (v: Visual) => JSON.stringify(v);
function addVisual(v: Visual) {
  liveVisuals.value = [v, ...liveVisuals.value].slice(0, 12);
}
watch(conversationId, () => {
  liveVisuals.value = [];
  hidden.value = new Set();
});
const board = computed(() => {
  // Newest answer first; within an answer in the order Schorschi showed them.
  const fromMessages = [...(conversation.data.value?.messages ?? [])].reverse().flatMap((m) => m.visuals ?? []);
  const seen = new Set<string>();
  return [...liveVisuals.value, ...fromMessages].filter((v) => {
    const k = visualKey(v);
    if (seen.has(k) || hidden.value.has(k)) return false;
    seen.add(k);
    return true;
  }).slice(0, 8);
});
function clearBoard() {
  hidden.value = new Set([...hidden.value, ...board.value.map(visualKey)]);
}

// --- chat replies and live events

function onReply(result: SendResult) {
  feel(result.reply.mood);
  if (result.reply.reaction) gesture(result.reply.reaction);
  else if (!result.reply.mood) gesture("nod");
  chatTool.value = null;
}

const off = onSchorschiEvent((e) => {
  if (e.kind === "gesture") {
    gesture(e.reaction);
    feel(e.mood, 20);
  } else if (e.kind === "activity") {
    if (e.conversationId && conversationId.value && e.conversationId !== conversationId.value) return;
    if (e.activity === "working") chatTool.value = e.tool ?? null;
    else if (e.activity) chatTool.value = null;
    feel(e.mood);
    if (e.reaction) gesture(e.reaction);
    if (e.visual) addVisual(e.visual);
  } else if (e.kind === "journal" && (e.entry.severity === "critical" || e.entry.severity === "warning")) {
    gesture("jump");
  }
});
onBeforeUnmount(off);

onMounted(() => window.setTimeout(() => gesture("wave"), 700));

function poke() {
  gesture(REACTIONS[Math.floor(Math.random() * REACTIONS.length)]);
}

// --- budget

const budget = computed(() => status.data.value?.budget);
const pct = (a: number, b: number) => (b > 0 ? Math.min(100, Math.round((a / b) * 100)) : 0);

// --- tabs (short labels on phones, where the row must not scroll away)

const phone = useMediaQuery("(max-width: 639.98px)");
const tabs = computed<PageTabItem[]>(() => [
  { id: "chat", label: copy.tabs.chat, icon: MessageSquare },
  { id: "feed", label: phone.value ? copy.tabsShort.feed : copy.tabs.feed, icon: Radar },
  { id: "proposals", label: copy.tabs.proposals, icon: CheckCheck, badge: openProposals.data.value?.length },
  { id: "memory", label: copy.tabs.memory, icon: Brain },
  ...(status.data.value?.canAdmin ? [{ id: "admin", label: phone.value ? copy.tabsShort.admin : copy.tabs.admin, icon: Settings2 }] : []),
]);

// --- the chat fills the screen below its top edge: the composer stays in view, the page does not scroll

const page = ref<HTMLElement | null>(null);
const chatPanel = ref<HTMLElement | null>(null);
/** Distance of the chat from the top of the document (px), or null until measured. */
const chatTop = ref<number | null>(null);
function measureChat() {
  const el = chatPanel.value;
  if (!el || tab.value !== "chat") return;
  const top = Math.round(el.getBoundingClientRect().top + window.scrollY);
  if (top > 0 && top !== chatTop.value) chatTop.value = top;
}
useResizeObserver(page, measureChat);
watch(tab, () => void nextTick(measureChat));
onMounted(() => void nextTick(measureChat));
</script>

<template>
  <div ref="page" class="flex flex-col gap-4 sm:gap-6">
    <!-- The tagline only from sm up: on a phone the stage and the chat need the room. -->
    <PageHeader :title="copy.title" :description="copy.tagline" class="max-sm:[&_.page-description]:hidden">
      <template #badges>
        <Badge v-if="status.data.value && !enabled" variant="muted" class="font-normal"><Moon /> {{ copy.asleep }}</Badge>
      </template>
    </PageHeader>

    <ErrorState v-if="status.error.value" :error="status.error.value" @retry="status.refetch()" />

    <div class="grid items-start gap-4 sm:gap-6 xl:grid-cols-[minmax(18rem,22rem)_minmax(0,1fr)]" :style="chatTop !== null ? { '--chat-top': `${chatTop}px` } : undefined">
      <!-- The stage and the board: a column beside the tabs on wide screens; on smaller ones the stage is a band above the tabs and the board follows them -->
      <!-- On xl it ends where the chat ends (--chat-top: the chat's top; the tabs and their gap sit above it). -->
      <aside class="contents xl:sticky xl:top-20 xl:flex xl:max-h-[calc(100dvh-var(--chat-top,16rem)+0.75rem)] xl:min-w-0 xl:flex-col xl:gap-4 xl:overflow-y-auto">
        <section
          class="stage relative order-1 grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-3 overflow-hidden rounded-2xl border p-3 shadow-xs sm:gap-x-6 sm:p-4 xl:flex xl:shrink-0 xl:flex-col xl:gap-3 xl:pt-6"
          aria-label="Schorschi"
        >
          <SchorschiAvatar
            :mood="mood"
            :activity="activity"
            :reaction="reaction"
            :speaking-level="voice.speakingLevel.value"
            :listening-level="voice.listeningLevel.value"
            size="var(--stage-avatar)"
            class="shrink-0 cursor-pointer [--stage-avatar:5.5rem] sm:[--stage-avatar:8rem] xl:[--stage-avatar:12.5rem] xl:[@media(max-height:56rem)]:[--stage-avatar:9rem]"
            @click="poke"
          />
          <div class="flex min-w-0 flex-col gap-2.5 xl:w-full xl:items-center">
            <div class="flex min-w-0 flex-col gap-0.5 xl:items-center xl:text-center" aria-live="polite">
              <p class="text-sm font-semibold">
                {{ enabled ? MOOD_LABELS[mood] : copy.asleep }}
                <span v-if="activityLabel" class="font-normal text-muted-foreground">· {{ activityLabel }}</span>
              </p>
              <p v-if="!enabled" class="text-xs text-muted-foreground">{{ copy.asleepHint }}</p>
              <p v-else-if="status.data.value?.mood.reason && !personalMood" class="line-clamp-2 text-xs text-muted-foreground xl:max-w-xs">{{ copy.moodBecause }} {{ status.data.value.mood.reason }}</p>
              <p v-else-if="status.data.value && !status.data.value.chat" class="text-xs text-muted-foreground xl:max-w-xs">{{ copy.noModel }}</p>
            </div>

            <!-- Voice: the kind of call and the button; a chat call runs right here -->
            <div v-if="enabled" class="flex min-w-0 flex-wrap items-center gap-2 xl:justify-center">
              <template v-if="!inCall || voice.mode.value !== 'chat'">
                <SchorschiModePicker v-model="callMode" :disabled="inCall || !canStartCall" />
                <Button
                  size="lg"
                  class="h-11 rounded-full px-4 sm:h-10"
                  :disabled="!canStartCall || inCall"
                  :title="!voice.supported ? copy.voice.unsupported : !voiceAvailable ? copy.voice.unavailable : `${copy.voice.modeHints[callMode]} ${copy.voice.privacy}`"
                  @click="startCall()"
                >
                  <Phone class="size-4" aria-hidden="true" />
                  <span class="sm:hidden">{{ copy.voice.startShort[callMode] }}</span>
                  <span class="hidden sm:inline">{{ copy.voice.start[callMode] }}</span>
                </Button>
              </template>
              <template v-else>
                <Button size="icon-lg" variant="outline" class="size-11 rounded-full" :aria-label="voice.muted.value ? copy.voice.unmute : copy.voice.mute" :aria-pressed="voice.muted.value" :disabled="!callLive" @click="voice.toggleMute()">
                  <MicOff v-if="voice.muted.value" class="size-5" aria-hidden="true" /><Mic v-else class="size-5" aria-hidden="true" />
                </Button>
                <Button size="lg" variant="destructive" class="h-11 rounded-full px-5" :disabled="voice.state.value === 'ending'" @click="voice.stop()">
                  <PhoneOff class="size-4" aria-hidden="true" /> {{ voice.state.value === "connecting" ? copy.voice.calling : copy.voice.hangUp }}
                </Button>
                <span v-if="callLive && remaining" class="text-xs text-muted-foreground tabular-nums">{{ copy.voice.left(remaining) }}</span>
                <span v-if="callLive && voice.protocol.value === 'live'" class="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">GPT-Live</span>
                <span v-if="callLive && turnTiming" class="text-[11px] text-muted-foreground tabular-nums" :title="copy.voice.turnTiming">{{ turnTiming }}</span>
              </template>
            </div>
            <p v-if="voice.error.value && !callView" class="text-xs text-destructive xl:text-center" role="alert">{{ voice.error.value }}</p>
            <p v-else-if="!inCall && voiceAvailable" class="hidden text-[11px] text-muted-foreground sm:block xl:max-w-xs xl:text-center">
              {{ copy.voice.modeHints[callMode] }} <span class="hidden xl:inline xl:[@media(max-height:56rem)]:hidden">{{ copy.voice.privacy }}</span>
            </p>
          </div>

          <ol v-if="inCall && voice.mode.value === 'chat' && (lines.length || voice.liveCaption.value)" class="col-span-2 flex w-full flex-col gap-1 text-xs" aria-label="Live transcript">
            <li v-for="(line, i) in lines.slice(-6)" :key="i" :class="cn('rounded-lg px-2 py-1', line.role === 'user' ? 'self-end bg-primary/10' : 'self-start bg-muted')">{{ line.text }}</li>
            <!-- What is being said right now; the final line replaces it -->
            <li
              v-if="voice.liveCaption.value"
              aria-live="polite"
              :class="cn('rounded-lg px-2 py-1 opacity-70', voice.liveCaption.value.role === 'user' ? 'self-end bg-primary/10' : 'self-start bg-muted', voice.liveCaption.value.cut && 'line-through decoration-muted-foreground/40')"
            >
              {{ voice.liveCaption.value.text }}…
            </li>
          </ol>

          <!-- Budget -->
          <dl v-if="budget" class="col-span-2 grid w-full grid-cols-2 gap-x-4 gap-y-2 border-t pt-2.5 text-[11px] text-muted-foreground xl:grid-cols-1 xl:pt-3">
            <div class="min-w-0">
              <div class="flex items-baseline justify-between gap-2">
                <dt class="truncate">{{ copy.budget.today }}</dt>
                <dd class="shrink-0 tabular-nums">{{ formatUsd(budget.spentTodayUsd) }} / {{ formatUsd(budget.dailyUsd) }}</dd>
              </div>
              <div class="mt-1 h-1.5 overflow-hidden rounded-full bg-muted"><div class="h-full rounded-full bg-chart-1" :style="{ width: `${pct(budget.spentTodayUsd, budget.dailyUsd)}%` }" /></div>
            </div>
            <div class="min-w-0">
              <div class="flex items-baseline justify-between gap-2">
                <dt class="truncate"><span class="sm:hidden">{{ copy.budget.yoursShort }}</span><span class="hidden sm:inline">{{ copy.budget.yours }}</span></dt>
                <dd class="shrink-0 tabular-nums">{{ formatUsd(budget.mineTodayUsd) }} / {{ formatUsd(budget.perPersonDailyUsd) }}</dd>
              </div>
              <div class="mt-1 h-1.5 overflow-hidden rounded-full bg-muted"><div class="h-full rounded-full bg-chart-2" :style="{ width: `${pct(budget.mineTodayUsd, budget.perPersonDailyUsd)}%` }" /></div>
            </div>
            <p v-if="budget.exhausted" class="col-span-full flex items-center gap-1 text-warning-foreground"><Moon class="size-3" aria-hidden="true" />{{ copy.budget.exhausted }}</p>
          </dl>
        </section>

        <!-- The board -->
        <section v-if="board.length" class="order-3 flex min-w-0 flex-col gap-2" :aria-label="copy.board.title">
          <header class="flex items-center gap-2 px-1">
            <h2 class="section-title">{{ copy.board.title }}</h2>
            <Button variant="ghost" size="xs" class="ml-auto" @click="clearBoard">{{ copy.board.clear }}</Button>
          </header>
          <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
            <VisualCard v-for="v in board" :key="visualKey(v)" :visual="v" />
          </div>
        </section>
      </aside>

      <!-- The tabs -->
      <div class="order-2 flex min-w-0 flex-col gap-4 sm:gap-5">
        <PageTabs v-model="tab" :items="tabs" variant="segmented" label="Schorschi" id-prefix="schorschi-tab" class="self-start" />
        <!-- The chat stays mounted (a draft or an answer on its way survive a look at the feed). -->
        <div
          v-show="tab === 'chat'"
          ref="chatPanel"
          role="tabpanel"
          aria-labelledby="schorschi-tab-chat"
        >
          <SchorschiChat v-model:conversation-id="conversationId" :can-talk="canTalk" :available="chatAvailable && enabled" :activity-label="activityLabel" @reply="onReply" @busy="(b) => (chatBusy = b)" />
        </div>
        <div v-if="tab === 'feed'" role="tabpanel" aria-labelledby="schorschi-tab-feed"><SchorschiFeed :can-talk="canTalk && enabled" /></div>
        <div v-else-if="tab === 'proposals'" role="tabpanel" aria-labelledby="schorschi-tab-proposals"><SchorschiProposals /></div>
        <div v-else-if="tab === 'memory'" role="tabpanel" aria-labelledby="schorschi-tab-memory"><SchorschiMemory /></div>
        <div v-else-if="tab === 'admin' && status.data.value?.canAdmin" role="tabpanel" aria-labelledby="schorschi-tab-admin"><SchorschiAdmin /></div>
      </div>
    </div>

    <SchorschiStandupView
      v-if="callView === 'standup'"
      :state="voice.state.value"
      :attempt="voice.attempt.value"
      :max-attempts="REDIAL_DELAYS.length"
      :muted="voice.muted.value"
      :speaking="voice.speaking.value || voice.speakingLevel.value > 0.06"
      :tool="voiceTool"
      :addressed="addressed"
      :lines="lines"
      :live-caption="voice.liveCaption.value"
      :visuals="board.slice(0, 2)"
      :remaining="remaining"
      :error="voice.error.value"
      :last-end="voice.lastEnd.value"
      :screen-on="voice.screenOn.value"
      :can-start="canStartCall"
      :can-talk="canTalk"
      :minutes-after-id="minutesAfterId"
      :mood="mood"
      :activity="activity"
      :reaction="reaction"
      :speaking-level="voice.speakingLevel.value"
      :listening-level="voice.listeningLevel.value"
      @start="startCall('standup', null)"
      @stop="voice.stop()"
      @mute="voice.toggleMute()"
      @close="closeCallView"
    />
    <SchorschiDriveView
      v-else-if="callView === 'drive'"
      :state="voice.state.value"
      :attempt="voice.attempt.value"
      :max-attempts="REDIAL_DELAYS.length"
      :muted="voice.muted.value"
      :speaking="voice.speaking.value || voice.speakingLevel.value > 0.06"
      :tool="voiceTool"
      :phase="voice.phase.value"
      :caption="liveCaption"
      :remaining="remaining"
      :error="voice.error.value"
      :ended="voice.lastEnd.value !== null"
      :screen-on="voice.screenOn.value"
      :can-start="canStartCall"
      :mood="mood"
      :activity="activity"
      :reaction="reaction"
      :speaking-level="voice.speakingLevel.value"
      :listening-level="voice.listeningLevel.value"
      @start="startCall('drive', voice.lastEnd.value?.conversationId ?? null)"
      @stop="voice.stop()"
      @mute="voice.toggleMute()"
      @close="closeCallView"
    />
  </div>
</template>

<style scoped>
.stage {
  background:
    radial-gradient(120% 70% at 50% 0%, color-mix(in oklch, var(--chart-4) 14%, transparent), transparent 70%),
    radial-gradient(90% 60% at 50% 100%, color-mix(in oklch, var(--chart-2) 12%, transparent), transparent 70%),
    var(--card);
}
</style>
